'use client'

import type { BaseRecord, HttpError } from '@refinedev/core'
import type { UseTableReturnType } from '@refinedev/react-table'
import type { Column, Row } from '@tanstack/react-table'
import { flexRender } from '@tanstack/react-table'
import { Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { Fragment, useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { DataTablePagination } from '~/components/refineUi/dataTable/dataTablePagination'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { cn } from '~/lib/utils'

type DataTableProps<TData extends BaseRecord> = {
  table: UseTableReturnType<TData, HttpError>
  /**
   * Content of a full-width row under an expanded row (`row.toggleExpanded()`;
   * the table needs `getRowCanExpand`). Without it rows never expand.
   */
  renderExpandedRow?: (row: Row<TData>) => ReactNode
  /** the table's accessible name, e.g. a section's table on a show page */
  'aria-label'?: string
}

export function DataTable<TData extends BaseRecord>({
  table,
  renderExpandedRow,
  'aria-label': ariaLabel,
}: DataTableProps<TData>) {
  const {
    reactTable: { getHeaderGroups, getRowModel, getAllColumns },
    refineCore: {
      tableQuery,
      currentPage,
      setCurrentPage,
      pageCount,
      pageSize,
      setPageSize,
    },
  } = table

  const columns = getAllColumns()
  const leafColumns = table.reactTable.getAllLeafColumns()
  const isLoading = tableQuery.isLoading
  const rows = getRowModel().rows

  const tableContainerRef = useRef<HTMLDivElement>(null)
  const tableRef = useRef<HTMLTableElement>(null)
  const [isOverflowing, setIsOverflowing] = useState({
    horizontal: false,
    vertical: false,
  })

  useEffect(() => {
    const checkOverflow = () => {
      if (tableRef.current && tableContainerRef.current) {
        const tableElement = tableRef.current
        const container = tableContainerRef.current

        const horizontalOverflow =
          tableElement.offsetWidth > container.clientWidth
        const verticalOverflow =
          tableElement.offsetHeight > container.clientHeight

        setIsOverflowing({
          horizontal: horizontalOverflow,
          vertical: verticalOverflow,
        })
      }
    }

    checkOverflow()

    // Check on window resize
    window.addEventListener('resize', checkOverflow)

    // Check when table data changes
    const timeoutId = setTimeout(checkOverflow, 100)

    return () => {
      window.removeEventListener('resize', checkOverflow)
      clearTimeout(timeoutId)
    }
  }, [tableQuery.data?.data, pageSize])

  return (
    <div className={cn('flex', 'flex-col', 'flex-1', 'gap-4')}>
      <div
        ref={tableContainerRef}
        className={cn('rounded-md', 'border')}
      >
        <Table
          ref={tableRef}
          aria-label={ariaLabel}
          style={{ tableLayout: 'fixed', width: '100%' }}
        >
          <TableHeader>
            {getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isPlaceholder = header.isPlaceholder

                  return (
                    <TableHead
                      key={header.id}
                      style={{
                        ...getCommonStyles({
                          column: header.column,
                          isOverflowing: isOverflowing,
                        }),
                      }}
                    >
                      {isPlaceholder ? null : (
                        <div className={cn('flex', 'items-center', 'gap-1')}>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </div>
                      )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className="relative">
            {isLoading ? (
              <>
                {Array.from({ length: pageSize < 1 ? 1 : pageSize }).map(
                  (_, rowIndex) => (
                    <TableRow
                      key={`skeleton-row-${rowIndex}`}
                      aria-hidden="true"
                    >
                      {leafColumns.map((column) => (
                        <TableCell
                          key={`skeleton-cell-${rowIndex}-${column.id}`}
                          style={{
                            ...getCommonStyles({
                              column,
                              isOverflowing: isOverflowing,
                            }),
                          }}
                          className={cn('truncate')}
                        >
                          <div className="h-8" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ),
                )}
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className={cn('absolute', 'inset-0', 'pointer-events-none')}
                  >
                    <Loader2
                      className={cn(
                        'absolute',
                        'top-1/2',
                        'left-1/2',
                        'animate-spin',
                        'text-primary',
                        'h-8',
                        'w-8',
                        '-translate-x-1/2',
                        '-translate-y-1/2',
                      )}
                    />
                  </TableCell>
                </TableRow>
              </>
            ) : null}
            {!isLoading &&
              rows.map((row) => {
                const cells = row.getVisibleCells()
                return (
                  <Fragment key={row.original?.id ?? row.id}>
                    <TableRow data-state={row.getIsSelected() && 'selected'}>
                      {cells.map((cell) => {
                        return (
                          <TableCell
                            key={cell.id}
                            style={{
                              ...getCommonStyles({
                                column: cell.column,
                                isOverflowing: isOverflowing,
                              }),
                            }}
                          >
                            <div className="truncate">
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </div>
                          </TableCell>
                        )
                      })}
                    </TableRow>
                    {renderExpandedRow !== undefined && row.getIsExpanded() && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell
                          colSpan={cells.length}
                          className="bg-muted/30 whitespace-normal"
                        >
                          {renderExpandedRow(row)}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                )
              })}
            {!isLoading && rows.length === 0 && (
              <DataTableNoData
                isOverflowing={isOverflowing}
                columnsLength={columns.length}
              />
            )}
          </TableBody>
        </Table>
      </div>
      {!isLoading && rows.length > 0 && (
        <DataTablePagination
          currentPage={currentPage}
          pageCount={pageCount}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          total={tableQuery.data?.total}
        />
      )}
    </div>
  )
}

function DataTableNoData({
  isOverflowing,
  columnsLength,
}: {
  isOverflowing: { horizontal: boolean; vertical: boolean }
  columnsLength: number
}) {
  const { t } = useTranslation()
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell
        colSpan={columnsLength}
        className={cn('relative', 'text-center')}
        style={{ height: '490px' }}
      >
        <div
          className={cn(
            'absolute',
            'inset-0',
            'flex',
            'flex-col',
            'items-center',
            'justify-center',
            'gap-2',
            'bg-background',
          )}
          style={{
            position: isOverflowing.horizontal ? 'sticky' : 'absolute',
            left: isOverflowing.horizontal ? '50%' : '50%',
            transform: 'translateX(-50%)',
            zIndex: isOverflowing.horizontal ? 2 : 1,
            width: isOverflowing.horizontal ? 'fit-content' : '100%',
            minWidth: '300px',
          }}
        >
          <div className={cn('text-lg', 'font-semibold', 'text-foreground')}>
            {t('shell.dataTable.emptyTitle')}
          </div>
          <div className={cn('text-sm', 'text-muted-foreground')}>
            {t('shell.dataTable.emptyText')}
          </div>
        </div>
      </TableCell>
    </TableRow>
  )
}

function pinnedShadow(
  overflowing: boolean,
  isLastLeftPinned: boolean,
  isFirstRightPinned: boolean,
) {
  if (overflowing && isLastLeftPinned) {
    return '-4px 0 4px -4px var(--border) inset'
  }
  if (overflowing && isFirstRightPinned) {
    return '4px 0 4px -4px var(--border) inset'
  }
  return undefined
}

export function getCommonStyles<TData>({
  column,
  isOverflowing,
}: {
  column: Column<TData>
  isOverflowing: {
    horizontal: boolean
    vertical: boolean
  }
}): React.CSSProperties {
  const isPinned = column.getIsPinned()
  const isLastLeftPinnedColumn =
    isPinned === 'left' && column.getIsLastColumn('left')
  const isFirstRightPinnedColumn =
    isPinned === 'right' && column.getIsFirstColumn('right')

  return {
    boxShadow: pinnedShadow(
      isOverflowing.horizontal,
      isLastLeftPinnedColumn,
      isFirstRightPinnedColumn,
    ),
    left:
      isOverflowing.horizontal && isPinned === 'left'
        ? `${column.getStart('left')}px`
        : undefined,
    right:
      isOverflowing.horizontal && isPinned === 'right'
        ? `${column.getAfter('right')}px`
        : undefined,
    opacity: 1,
    position:
      isOverflowing.horizontal && isPinned !== false ? 'sticky' : 'relative',
    background:
      isOverflowing.horizontal && isPinned !== false ? 'var(--background)' : '',
    borderTopRightRadius:
      isOverflowing.horizontal && isPinned === 'right'
        ? 'var(--radius)'
        : undefined,
    borderBottomRightRadius:
      isOverflowing.horizontal && isPinned === 'right'
        ? 'var(--radius)'
        : undefined,
    borderTopLeftRadius:
      isOverflowing.horizontal && isPinned === 'left'
        ? 'var(--radius)'
        : undefined,
    borderBottomLeftRadius:
      isOverflowing.horizontal && isPinned === 'left'
        ? 'var(--radius)'
        : undefined,
    width: column.getSize(),
    zIndex: isOverflowing.horizontal && isPinned !== false ? 1 : 0,
  }
}

DataTable.displayName = 'DataTable'
